import { getInsuranceTypeDisplayName, IQuote, QuoteTypeCodes } from '@/interfaces';
import { useAnalyticsStore } from '@/store';
import { PaymentFrequency } from '@/enums/payment-frequency';
import { PaymentTypes } from '@/enums/payment-types.enum';
import { YES, NO } from '@/constants/general-words.const';

import {
  IFormAnalytics,
  IAnalyticsForm,
  IAnalyticsProfileInfo,
  IAnalyticsDetail,
  IAnalyticsPaymentDetail,
  IAnalyticsNavigation,
} from '@/interfaces/forms/analytics-interface';
import { FormEvents } from '@/enums/form-events.enum';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { format } from 'date-fns';
import { convertISOToDateFormatted } from './data-transforms';

export class FormAnalyticsBuilder {
  private readonly _quote: Partial<IQuote>;
  formAnalytics: IFormAnalytics;

  analyticsStore = useAnalyticsStore();

  constructor(quote: Partial<IQuote>, event: FormEvents, translation: any) {
    const dictionary: Record<string, string> = translation.getLocaleMessage('en');
    this._quote = quote;

    this.formAnalytics = {} as IFormAnalytics;
    this.formAnalytics.form = {} as IAnalyticsForm;
    this.formAnalytics.event = event;

    if (event !== FormEvents.JEOPARDY) {
      this.formAnalytics.form.referenceID = quote.reference ?? '';

      if (quote.quoteType) {
        this.formAnalytics.form.productName = getInsuranceTypeDisplayName(dictionary, QuoteTypeCodes[quote.quoteType]);
      }
    }
  }

  populateProfileInfo() {
    // Ensure profileInfo is always initialized
    // Contact ID is handled in analytic.state
    if (!this.formAnalytics.profileInfo) {
      this.formAnalytics.profileInfo = {} as IAnalyticsProfileInfo;
    }

    this.formAnalytics.profileInfo.insuranceHoldings =
      this._quote.primaryPolicyHolderDetails?.numberOfExistingPoliciesWithRacq?.value?.toString() ?? '';
    // Will be overwritten in analytics.state if membership level is undefined.
    this.formAnalytics.profileInfo.memberLevel = this._quote.primaryPolicyHolderDetails?.hasRacqMembership?.value
      ? this._quote.primaryPolicyHolderDetails?.membershipLoyaltyLevel?.value
      : 'Not a member';
    this.formAnalytics.profileInfo.postcode =
      this._quote.primaryPolicyHolderDetails?.postcode?.value ??
      this._quote.homeAndContentsQuote?.homeLocationDetails?.postCode?.value ??
      this._quote.motorQuote?.vehicleLocationDetails?.postcode?.value ??
      this._quote.petsQuote?.pets?.at(0)?.postcode?.value ??
      '';
  }

  populateProfileInfoForCarPurchase() {
    this.populateProfileInfo();

    const driverDateOfBirth = this._quote.motorQuote?.primaryDriverDetails?.driverDateOfBirth?.value;

    // Ensure profileInfo exists before modifying it
    if (!this.formAnalytics.profileInfo) {
      this.formAnalytics.profileInfo = {} as IAnalyticsProfileInfo;
    }

    this.formAnalytics.profileInfo.birthYear = driverDateOfBirth
      ? convertISOToDateFormatted(driverDateOfBirth).split('/')[2]
      : undefined;
  }

  populateHomeContentsBuildingDetails() {
    this.formAnalytics.form.detail = {} as IAnalyticsDetail;
    this.formAnalytics.form.detail.buildingExcess =
      this._quote.homeAndContentsQuote?.homeQuote?.standardExcess?.value ?? 0;
    this.formAnalytics.form.detail.contentsExcess =
      this._quote.homeAndContentsQuote?.contentsQuote?.standardExcess?.value ?? 0;
    this.formAnalytics.form.detail.excess = this.calcTotalExcess(this._quote) ?? '';
    this.formAnalytics.form.detail.contentsCost =
      this._quote.homeAndContentsQuote?.contentsQuote?.generalContentsReplacementAmount?.value?.toString() ?? '0';
    this.formAnalytics.form.detail.homeValue =
      this._quote.homeAndContentsQuote?.homeQuote?.totalRebuildCost?.value?.toString() ?? '0';
    this.formAnalytics.form.detail.occupancyType = this._quote.homeAndContentsQuote?.propertyOwnershipType?.value ?? '';

    const optionalBenefits = [];
    if (this._quote.homeAndContentsQuote?.advancedCoverOptions?.advancedCoverType?.value) {
      const split = this._quote.homeAndContentsQuote.advancedCoverOptions.advancedCoverType.value.split('&');
      for (const item of split) {
        if (item.includes('MB')) {
          optionalBenefits.push('Motor burnout cover');
        } else {
          optionalBenefits.push('Advanced cover');
        }
      }
    }
    this.formAnalytics.form.detail.optionalBenefits = optionalBenefits.length ? optionalBenefits.join(', ') : 'None';
  }

  populateProfileInfoForPetPurchase() {
    this.populateProfileInfo();

    const primaryPolicyHolderDob = this._quote.primaryPolicyHolderDetails?.dateOfBirth?.value;

    // Ensure profileInfo exists before modifying it
    if (!this.formAnalytics.profileInfo) {
      this.formAnalytics.profileInfo = {} as IAnalyticsProfileInfo;
    }

    if (primaryPolicyHolderDob) {
      this.formAnalytics.profileInfo.birthYear = convertISOToDateFormatted(primaryPolicyHolderDob).split('/')[2];
    }
  }

  populatePaymentDetails(isActualPayment?: boolean, creditCardProvider?: string) {
    const paymentFrequency = PaymentFrequency[this._quote.paymentFrequency ?? 0];
    let amountPaid;
    if (this._quote?.paymentFrequency === PaymentFrequency.Annually) {
      amountPaid = this._quote?.insuranceQuoteSummary?.yearlyAnnualPremium as number;
    } else {
      amountPaid = this._quote?.insuranceQuoteSummary?.monthlyDeposit as number;
    }

    this.formAnalytics.payment = {} as IAnalyticsPaymentDetail;
    this.formAnalytics.payment.paymentID = this._quote?.paymentReferenceNumber ?? '';
    this.formAnalytics.payment.amountAnnual = this._quote.insuranceQuoteSummary?.yearlyAnnualPremium ?? 0;
    this.formAnalytics.payment.amountPaid = amountPaid ?? 0;
    this.formAnalytics.payment.paymentFrequency = paymentFrequency ?? '';

    if (this._quote.insuranceStartDate?.value) {
      this.formAnalytics.payment.policyStartDate = format(this._quote.insuranceStartDate.value, STANDARD_DATE_FORMAT);
      this.formAnalytics.payment.policyStartFuture =
        this._quote.insuranceStartDate.value > new Date() ? 'true' : 'false';
    }

    if (isActualPayment) {
      this.formAnalytics.payment.autoRenew = this._quote?.renewalPreference ? 'On' : 'Off';

      if (creditCardProvider) {
        this.formAnalytics.payment.creditCardProvider = creditCardProvider ?? '';
      }

      if (this._quote.paymentType) {
        this.formAnalytics.payment.paymentMethod = PaymentTypes[this._quote.paymentType] ?? '';
      }

      this.formAnalytics.payment.policyBind =
        this._quote.policyReference && this._quote.policyReference?.length > 0 ? YES : NO;
    }
  }

  populateNavigation(name: string, url: string) {
    this.formAnalytics.navigation = {} as IAnalyticsNavigation;
    this.formAnalytics.navigation.name = name;
    this.formAnalytics.navigation.URL = url;
  }

  populateJeopardy(errorType: string, errorDetails: string) {
    this.formAnalytics.form.formErrorType = errorType;
    this.formAnalytics.form.formErrorDetail = errorDetails;
  }

  populateFormField(field: string) {
    this.formAnalytics.form.field = field;
  }

  populatePaymentAttempt(paymentAttempt: number) {
    this.formAnalytics.payment = {} as IAnalyticsPaymentDetail;
    this.formAnalytics.payment.paymentAttempt = paymentAttempt?.toString();
  }

  calcTotalExcess(quote: Partial<IQuote>) {
    return (
      (quote.homeAndContentsQuote?.homeQuote?.standardExcess?.value ?? 0) +
      (quote.homeAndContentsQuote?.contentsQuote?.standardExcess?.value ?? 0)
    );
  }

  pushAnalytics(appendToStepName?: string) {
    this.analyticsStore.pushAnalytics(this.formAnalytics, appendToStepName);
  }

  // Car Purchase Specific
  populateCarDetails(isConfirmationStep?: boolean) {
    this.formAnalytics.form.detail = {} as IAnalyticsDetail;
    this.formAnalytics.form.detail.bodyType =
      this._quote.motorQuote?.vehicleDetails.glassesGuideVehicleDetails.body.description;
    this.formAnalytics.form.detail.fuelType =
      this._quote.motorQuote?.vehicleDetails.glassesGuideVehicleDetails.fuelType.value;
    this.formAnalytics.form.detail.makeName =
      this._quote.motorQuote?.vehicleDetails.glassesGuideVehicleDetails.make.description;
    this.formAnalytics.form.detail.modelName =
      this._quote.motorQuote?.vehicleDetails.glassesGuideVehicleDetails.model.description;
    if (this._quote.motorQuote?.standardExcess?.value)
      this.formAnalytics.form.detail.excess = this._quote.motorQuote.standardExcess.value;
    if (this._quote.motorQuote?.vehicleDetails.financeCode?.value)
      this.formAnalytics.form.detail.financeType = this._quote.motorQuote.vehicleDetails.financeCode.value;

    const optionalBenefits = [];
    if (this._quote.motorQuote?.quoteOptions?.includeCarHire?.value) {
      optionalBenefits.push('Hire Car');
    }

    if (this._quote.motorQuote?.quoteOptions?.includeReplacementWindscreenCover?.value) {
      optionalBenefits.push('Windscreen');
    }
    this.formAnalytics.form.detail.optionalBenefits = optionalBenefits.length ? optionalBenefits.join(', ') : 'None';

    this.formAnalytics.form.detail.valueCategory = this._quote.motorQuote?.quoteOptions?.agreedVehicleValue?.value
      ? 'Agreed value'
      : 'Market value';
    this.formAnalytics.form.detail.vehicleValue =
      this._quote.motorQuote?.vehicleDetails.glassesGuideVehicleDetails.vehicleValue?.value.toString();

    if (this._quote.motorQuote?.quoteOptions?.agreedVehicleValue?.value) {
      this.formAnalytics.form.detail.agreedValue =
        this._quote.motorQuote.quoteOptions.agreedVehicleValue.value.toString();
    }
    this.formAnalytics.form.detail.vehicleYear =
      this._quote.motorQuote?.vehicleDetails.glassesGuideVehicleDetails.manufacturingYear?.year.toString();

    if (isConfirmationStep) {
      const numberOfDrivers = 1 + (this._quote.motorQuote?.additionalDriverDetailsCount.value ?? 0);
      this.formAnalytics.form.detail.numberOfDrivers = `${numberOfDrivers} driver listed`;
    }
  }

  populateCarPaymentDetails(isActualPayment?: boolean, creditCardProvider?: string) {
    try {
      const paymentFrequency = PaymentFrequency[this._quote.paymentFrequency ?? 0];
      const payment: Partial<IAnalyticsPaymentDetail> = {
        paymentFrequency: paymentFrequency,
        policyStartDate: format(this._quote.termStartDate ?? '', STANDARD_DATE_FORMAT),
        policyStartFuture: this._quote.termStartDate && this._quote.termStartDate > new Date() ? 'true' : 'false',
      };
      this.formAnalytics.payment = payment;

      let amountPaid;
      if (this._quote.paymentFrequency === PaymentFrequency.Annually) {
        amountPaid = this._quote.insuranceQuoteSummary?.yearlyAnnualPremium as number;
      } else {
        amountPaid = this._quote.insuranceQuoteSummary?.monthlyDeposit as number;
      }
      this.formAnalytics.form.amount = amountPaid;
      this.formAnalytics.form.referenceID = this._quote.reference;

      if (isActualPayment) {
        this.formAnalytics.payment.amountPaid = amountPaid;
        this.formAnalytics.payment.autoRenew = this._quote?.renewalPreference ? 'On' : 'Off';

        if (creditCardProvider) {
          this.formAnalytics.payment.creditCardProvider = creditCardProvider ?? '';
        }

        if (this._quote.paymentType) {
          this.formAnalytics.payment.paymentMethod = PaymentTypes[this._quote.paymentType];
        }

        this.formAnalytics.payment.paymentID = this._quote?.paymentReferenceNumber ?? '';

        this.formAnalytics.payment.policyBind =
          this._quote.policyReference && this._quote.policyReference?.length > 0 ? YES : NO;
      }
    } catch {
      // do nothing
    }
  }
}
